import { TwAutocompleteOption } from '../../components/TwAutocomplete/TwAutocomplete';
import { INotifyDriverWithFullSegment } from '../../components/TwNotifyDriver/TwNotifyDriver';
import { DRIVER_DEFAULT_PAY_RATE_TYPE, DRIVER_TYPE } from '../../redux/api/driver/driver.model';
import { TimeOffReason } from '../../redux/api/timeOff/timeOff.model';
import { Trip, TRIP_DRIVER_PAY_UNIT } from '../../redux/api/trips/trips.model';
import StringUtils from '../stringUtils';

class DriverUtils {
  /**
   * Returns an array of autocomplete options of driver types
   * @returns
   */
  static typesToAutocompleteOptions = (): TwAutocompleteOption[] => {
    const options = Object.entries(DRIVER_TYPE).map(([key, value]) => {
      return {
        id: key,
        label: StringUtils.getDriverTypeLabel(value),
        value: value
      };
    });

    return options;
  };

  /**
   * Returns an option (TwAutocompleteOption) of driver type
   * @param type
   * @returns
   */
  static typeToAutocompleteOption = (type: DRIVER_TYPE): TwAutocompleteOption => {
    return {
      id: type,
      label: StringUtils.getDriverTypeLabel(type),
      value: type
    };
  };

  /**
   * Returns an array of autocomplete options of driver types
   * @returns
   */
  static payRateTypesToAutocompleteOptions = (): TwAutocompleteOption[] => {
    const filteredOptions = Object.entries(DRIVER_DEFAULT_PAY_RATE_TYPE);

    const options = filteredOptions.map(([key, value]) => {
      return {
        id: key,
        label: StringUtils.getDriverPayRateTypeLabel(value),
        value: value
      };
    });

    return options;
  };

  /**
   * Returns an option (TwAutocompleteOption) of driver type
   * @param payRateType
   * @returns
   */
  static payRateTypeToAutocompleteOption = (
    payRateType: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT
  ): TwAutocompleteOption => {
    return {
      id: payRateType,
      label: StringUtils.getDriverPayRateTypeLabel(payRateType),
      value: payRateType
    };
  };

  /**
   * Returns an array of objects that contains the drivers that are not in the original array
   * and the Trip information
   * @param original Trip[]
   * @param incoming Trip[]
   * @returns INotifyDriverWithFullSegment[]
   */
  static findDifferenceOfDriversToAssign = (
    original: Trip[],
    incoming: Trip[]
  ): INotifyDriverWithFullSegment[] => {
    const diff: INotifyDriverWithFullSegment[] = [];

    // if nothing in original, then all drivers are new
    if (original.length === 0 && incoming.length > 0) {
      const tripsWithDrivers = incoming.filter((trip) => trip.drivers.length > 0);

      tripsWithDrivers.forEach((trip) => {
        trip.drivers.forEach((driver, driverIndex) => {
          const diffDriver: INotifyDriverWithFullSegment = {
            driverId: driver.id,
            driverIndex,
            fullSegment: trip,
            isForExistingLoadUpdate: false
          };

          diff.push(diffDriver);
        });
      });
    }

    // Both have data so we find the difference
    if (original.length > 0 && incoming.length > 0) {
      incoming.forEach((incomingSegment, segmentIndex) => {
        const originalSegment = original[segmentIndex];

        // Check if any load stops have changed in the segment
        const areLoadStopsChanged =
          !originalSegment ||
          incomingSegment.stops.some((incomingLoadStop, loadStopIndex) => {
            const originalLoadStop = originalSegment.stops[loadStopIndex];
            return (
              !originalLoadStop ||
              incomingLoadStop.location_id !== originalLoadStop.location_id ||
              incomingLoadStop.start_time !== originalLoadStop.start_time ||
              incomingLoadStop.end_time !== originalLoadStop.end_time ||
              incomingLoadStop.start_date !== originalLoadStop.start_date ||
              incomingLoadStop.end_date !== originalLoadStop.end_date
            );
          });

        incomingSegment.drivers.forEach((incomingDriver, driverIndex) => {
          const isDriverAdded =
            !originalSegment ||
            !originalSegment.drivers.some(
              (originalDriver) => originalDriver.id === incomingDriver.id
            );

          if (isDriverAdded || areLoadStopsChanged) {
            const diffDriver: INotifyDriverWithFullSegment = {
              driverId: incomingDriver.id,
              driverIndex,
              fullSegment: incomingSegment,
              isForExistingLoadUpdate: !isDriverAdded
            };

            diff.push(diffDriver);
          }
        });
      });
    }

    return diff;
  };

  static findUnassignedDrivers = (
    original: Trip[],
    incoming: Trip[]
  ): INotifyDriverWithFullSegment[] => {
    const unassignedDrivers: INotifyDriverWithFullSegment[] = [];

    original.forEach((orig, segmentIndex) => {
      const incomingSegment = incoming[segmentIndex];

      orig.drivers.forEach((originalDriver, driverIndex) => {
        const isDriverAssigned =
          !!incomingSegment &&
          incomingSegment.drivers.some((incomingDriver) => incomingDriver.id === originalDriver.id);

        if (!incomingSegment || !isDriverAssigned) {
          const unassignedDriver: INotifyDriverWithFullSegment = {
            driverId: originalDriver.id,
            driverIndex: driverIndex,
            fullSegment: orig,
            deleted_trip_index: segmentIndex
          };

          unassignedDrivers.push(unassignedDriver);
        }
      });
    });

    return unassignedDrivers;
  };

  /** Returns an option (TwAutocompleteOption) of driver type based on the type string
   * @param type
   * @returns
   */
  static getPayTypeOption = (type: string | undefined): TwAutocompleteOption | undefined => {
    if (type && Object.keys(DRIVER_DEFAULT_PAY_RATE_TYPE).includes(type)) {
      const indexOfValue = Object.keys(DRIVER_DEFAULT_PAY_RATE_TYPE).indexOf(type);
      const val = Object.values(DRIVER_DEFAULT_PAY_RATE_TYPE)[indexOfValue];
      return { id: val, label: StringUtils.getDriverPayRateTypeLabel(val), value: val };
    }
    if (type) return { id: type, label: type, value: type };
    return undefined;
  };

  /** Returns the time off reason in a formatted string
   * @param reason
   * @returns
   */
  static formatReason = (reason: TimeOffReason): string => {
    return reason.charAt(0).toUpperCase() + reason.slice(1).toLowerCase();
  };
}

export default DriverUtils;
